/**
 * CREATIVE DETONATOR - Haptics Utility
 * Provides tactile feedback for mobile success moments
 *
 * Uses the Vibration API (navigator.vibrate) for haptic feedback
 * Falls back gracefully on devices that don't support it
 *
 * @version 2.0 - Enhanced with visual feedback and auto-attach
 */

import { createLogger } from '../core/logger.js';

const log = createLogger('haptics');

/**
 * Check if haptics are supported
 * @returns {boolean}
 */
function isSupported() {
  return 'vibrate' in navigator;
}

/**
 * Check if we're on a mobile device
 * @returns {boolean}
 */
function isMobile() {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 2) ||
    window.matchMedia('(max-width: 768px)').matches;
}

/**
 * Haptic feedback patterns (in milliseconds)
 * Each pattern is an array of [vibrate, pause, vibrate, pause, ...]
 */
const PATTERNS = {
  // Short tap feedback (like button press)
  tap: [10],

  // Light tap (very subtle)
  light: [5],

  // Medium tap
  medium: [15],

  // Heavy tap
  heavy: [25],

  // Success feedback (like save complete)
  success: [15, 50, 15],

  // Error feedback (shake pattern)
  error: [50, 30, 50, 30, 50],

  // Warning feedback
  warning: [30, 50, 30],

  // Selection change
  select: [8],

  // Multi-select (adding to selection)
  multiSelect: [8, 30, 12],

  // Long press recognition
  longPress: [20],

  // Double tap confirmation
  doubleTap: [10, 40, 10],

  // Navigation feedback
  navigate: [12],

  // Phase transition
  phaseChange: [15, 30, 25],

  // Detonation complete (celebration!)
  detonation: [20, 40, 20, 40, 30, 60, 40],

  // Card flip/reveal
  reveal: [10, 20, 15, 20, 20],

  // Delete confirmation
  delete: [40, 30, 40],

  // Refresh/reload
  refresh: [15, 40, 15, 40, 15]
};

/**
 * Trigger haptic feedback
 * @param {string} pattern - Pattern name from PATTERNS
 * @returns {boolean} - Whether haptic was triggered
 */
function vibrate(pattern = 'tap') {
  if (!isSupported()) {
    log.debug('Haptics not supported');
    return false;
  }

  // Only trigger on mobile devices
  if (!isMobile()) {
    log.debug('Haptics skipped - not mobile');
    return false;
  }

  const vibrationPattern = PATTERNS[pattern] || PATTERNS.tap;

  try {
    navigator.vibrate(vibrationPattern);
    log.debug(`Haptic: ${pattern}`);
    return true;
  } catch (error) {
    log.warn('Haptic failed:', error.message);
    return false;
  }
}

/**
 * Cancel any ongoing vibration
 */
function cancel() {
  if (isSupported()) {
    navigator.vibrate(0);
  }
}

/**
 * Add visual feedback class to element
 * @param {HTMLElement} element - Element to add feedback to
 * @param {string} type - Type of feedback (success, error, select)
 * @param {number} duration - How long to show the feedback (ms)
 */
function visualFeedback(element, type = 'success', duration = 300) {
  if (!element) return;

  const className = `haptic-${type}`;
  element.classList.add(className);

  setTimeout(() => {
    element.classList.remove(className);
  }, duration);
}

/**
 * Combined haptic and visual feedback
 * @param {string} pattern - Haptic pattern name
 * @param {HTMLElement} element - Element for visual feedback (optional)
 */
function feedback(pattern, element = null) {
  vibrate(pattern);

  if (element) {
    const visualType = pattern === 'error' ? 'error' :
      pattern === 'success' || pattern === 'detonation' ? 'success' :
        'select';
    visualFeedback(element, visualType);
  }
}

/**
 * Auto-attach haptic feedback to common interactive elements
 * Call this once on page load to enable haptics for buttons, cards, etc.
 * @param {HTMLElement} container - Container to scan for elements (default: document)
 */
function autoAttach(container = document) {
  if (!isMobile()) return;

  // Button clicks
  container.addEventListener('click', (e) => {
    const btn = e.target.closest('button, .btn, [role="button"]');
    if (btn && !btn.disabled) {
      vibrate('tap');
    }
  }, { passive: true });

  // Card selections
  container.addEventListener('click', (e) => {
    const card = e.target.closest('.idea-card, .trigger-card, .cartridge, .format-card');
    if (card) {
      const isSelecting = !card.classList.contains('selected');
      vibrate(isSelecting ? 'select' : 'light');
    }
  }, { passive: true });

  // Nav items
  container.addEventListener('click', (e) => {
    const navItem = e.target.closest('.nav-item, .profile-item, .canvas-tab-btn');
    if (navItem) {
      vibrate('navigate');
    }
  }, { passive: true });

  // Toggle switches and checkboxes
  container.addEventListener('change', (e) => {
    if (e.target.type === 'checkbox' || e.target.classList.contains('toggle')) {
      vibrate('select');
    }
  }, { passive: true });

  log.info('Haptics auto-attached');
}

/**
 * Attach haptics to a specific scope (useful for dynamically added content)
 * @param {HTMLElement} scope - The scope element to attach haptics to
 */
function attachToScope(scope) {
  if (!isMobile() || !scope) return;

  // Add touch feedback to all buttons in scope
  const buttons = scope.querySelectorAll('button, .btn, [role="button"]');
  buttons.forEach(btn => {
    btn.addEventListener('touchstart', () => vibrate('light'), { passive: true });
  });

  // Add touch feedback to selectable cards
  const cards = scope.querySelectorAll('.idea-card, .trigger-card, .cartridge');
  cards.forEach(card => {
    card.addEventListener('touchstart', () => {
      const isSelected = card.classList.contains('selected');
      if (!isSelected) {
        vibrate('light');
      }
    }, { passive: true });
  });
}

/**
 * Haptics API
 */
export const Haptics = {
  isSupported,
  isMobile,
  vibrate,
  cancel,
  visualFeedback,
  feedback,
  autoAttach,
  attachToScope,

  // Convenience methods
  tap: () => vibrate('tap'),
  light: () => vibrate('light'),
  medium: () => vibrate('medium'),
  heavy: () => vibrate('heavy'),
  success: () => vibrate('success'),
  error: () => vibrate('error'),
  warning: () => vibrate('warning'),
  select: () => vibrate('select'),
  multiSelect: () => vibrate('multiSelect'),
  longPress: () => vibrate('longPress'),
  doubleTap: () => vibrate('doubleTap'),
  navigate: () => vibrate('navigate'),
  phaseChange: () => vibrate('phaseChange'),
  detonation: () => vibrate('detonation'),
  reveal: () => vibrate('reveal'),
  delete: () => vibrate('delete'),
  refresh: () => vibrate('refresh')
};

export default Haptics;
